import {
  hasModulePermission,
  PERMISSION_LEVEL_LABELS,
  PERMISSION_MODULE_LABELS,
  type PermissionLevelValue,
  type PermissionModule,
} from '@crm/shared';
import { Button, Spinner } from '@crm/ui';
import { ShieldAlert } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AUTH_ROUTES } from '@/modules/auth/constants';
import { useSession } from '@/modules/auth/hooks';

interface PermissionRouteProps {
  module: PermissionModule;
  level?: PermissionLevelValue;
}

/**
 * Guarda de rota por permissao. A API continua sendo quem decide de fato -
 * isto evita abrir uma tela que so mostraria erro 403.
 */
export function RequirePermission({ module, level = 'READ' }: PermissionRouteProps) {
  const { permissions, user, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (!permissions || !hasModulePermission(permissions, module, level)) {
    return (
      <div className="flex flex-col items-center gap-4 px-4 pt-12 text-center sm:pt-20">
        <span className="flex h-32 w-32 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <ShieldAlert className="h-16 w-16" />
        </span>
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Erro 403
        </p>
        <p className="text-3xl font-bold tracking-tight text-destructive">Acesso restrito</p>

        <dl className="grid w-full max-w-xs grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 rounded-card border border-border bg-muted/40 px-4 py-3.5 text-left text-sm">
          <dt className="text-muted-foreground">Pagina</dt>
          <dd className="truncate text-right font-medium">{location.pathname}</dd>
          <dt className="text-muted-foreground">Permissao exigida</dt>
          <dd className="truncate text-right font-medium">
            {PERMISSION_LEVEL_LABELS[level]} em {PERMISSION_MODULE_LABELS[module]}
          </dd>
          <dt className="text-muted-foreground">Conta logada</dt>
          <dd className="truncate text-right font-medium">{user?.email ?? '-'}</dd>
        </dl>

        <p className="max-w-xs text-sm text-muted-foreground">
          Fale com um administrador para receber um perfil que conceda esse acesso.
        </p>

        <Button asChild variant="outline" size="lg" className="mt-1 w-full sm:w-auto">
          <Link to={AUTH_ROUTES.DASHBOARD}>Voltar ao inicio</Link>
        </Button>
      </div>
    );
  }

  return <Outlet />;
}
